// lazyWithRetry — a resilient drop-in for React.lazy.
//
// Why this exists (#261, Gap 6): a dynamic `import()` can reject for reasons
// that have nothing to do with a real bug:
//   • the chunk 404s because the app was redeployed and the hashed filename
//     the running tab remembers no longer exists on the CDN, or
//   • the network blipped mid-fetch on flaky mobile.
// A bare `React.lazy` lets that rejection bubble straight to the router's
// `errorElement` → the full-page "Oops" with no way forward. This wrapper
// retries the import once after a short delay (most transient blips clear by
// then), and if it STILL fails, throws an Error tagged `ChunkLoadError` so a
// surrounding <ChunkErrorBoundary> can offer a "reload to get the new version"
// affordance instead of a dead end.
//
// Load-path only (#261): this code runs solely when a route component is first
// imported. It adds no per-frame work and never touches the render/WebGL path —
// zero effect on viewer/RTX rendering.
import { lazy, type ComponentType } from 'react';

/** Marker name stamped onto the failure we throw after the retry is exhausted. */
export const CHUNK_LOAD_ERROR_NAME = 'ChunkLoadError';

/**
 * Heuristic, framework-agnostic predicate: does this error look like a failed
 * dynamic-import / chunk fetch (as opposed to a genuine bug inside the loaded
 * module)? Pure and dependency-free so it can be unit-tested in isolation and
 * reused by the error boundary.
 *
 * Matches either:
 *   • an Error whose `name` is our `ChunkLoadError` marker (what we throw), or
 *   • any value whose stringified message matches the known browser/bundler
 *     phrasings for a chunk/module load failure or a network fetch failure.
 */
export function isChunkLoadError(err: unknown): boolean {
  if (err && typeof err === 'object' && 'name' in err) {
    const name = (err as { name?: unknown }).name;
    if (name === CHUNK_LOAD_ERROR_NAME) return true;
  }

  const message =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message)
          : '';

  // Phrasings observed across Webpack, Vite/Rollup, and the browser fetch layer:
  //   "Loading chunk 5 failed"                     (Webpack)
  //   "Failed to fetch dynamically imported module" (Vite / native ESM)
  //   "error loading dynamically imported module"   (Vite variant)
  //   "Importing a module script failed"            (Safari)
  //   "Failed to fetch"                             (network drop)
  return /Loading chunk|dynamically imported module|module script failed|Failed to fetch/i.test(
    message,
  );
}

export interface LazyWithRetryOptions {
  /** Delay (ms) before the single retry. Default 400ms. */
  retryDelayMs?: number;
  /** Number of retries after the initial attempt. Default 1. */
  retries?: number;
}

/**
 * Wraps a dynamic-import factory so the import is retried before giving up.
 * Exported separately from {@link lazyWithRetry} so the retry behaviour can be
 * unit-tested without rendering a React tree.
 *
 * On the final failure the original rejection is re-thrown with its `name`
 * forced to `ChunkLoadError`, so {@link isChunkLoadError} (and the boundary)
 * recognise it regardless of the underlying bundler's wording.
 */
export async function importWithRetry<T>(
  factory: () => Promise<T>,
  { retryDelayMs = 400, retries = 1 }: LazyWithRetryOptions = {},
): Promise<T> {
  let lastError: unknown;
  // attempts = 1 initial + `retries` follow-ups.
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await factory();
    } catch (err) {
      lastError = err;
      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
      }
    }
  }

  // Out of retries — surface a tagged error the boundary can recognise.
  const tagged =
    lastError instanceof Error
      ? lastError
      : new Error(
          typeof lastError === 'string'
            ? lastError
            : 'Failed to load dynamically imported module',
        );
  tagged.name = CHUNK_LOAD_ERROR_NAME;
  throw tagged;
}

/**
 * Drop-in replacement for `React.lazy` that retries the dynamic import once
 * (configurable) before failing, and tags the final failure as a
 * `ChunkLoadError`. Use anywhere you would call `React.lazy(() => import(...))`.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  opts?: LazyWithRetryOptions,
) {
  return lazy(() => importWithRetry(factory, opts));
}
